import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Plus, Trash2, Copy, Search, BookOpen, Tag, AtSign, Pencil, Check, X, PenSquare } from "lucide-react";
import { Facebook, Instagram, Linkedin, Twitter } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

const PLATFORM_ICONS: Record<string, React.ElementType> = {
  instagram: Instagram, twitter: Twitter, linkedin: Linkedin,
  facebook: Facebook, threads: AtSign,
};

const PLATFORM_COLORS: Record<string, string> = {
  instagram: "#e1306c", twitter: "#1da1f2", linkedin: "#0077b5",
  facebook: "#1877f2", threads: "#101010", all: "#6b7280",
};

const CATEGORIES = ["Caption", "Hook", "CTA", "Bio", "Thread", "Story", "Ad Copy", "Announcement", "Engagement", "Educational"];

interface Template {
  id: string;
  title: string;
  content: string;
  platform: string;
  category: string;
  tags: string[];
  use_count: number;
  created_at: string;
}

interface ContentLibraryViewProps {
  onUseInCompose?: (content: string) => void;
}

export function ContentLibraryView({ onUseInCompose }: ContentLibraryViewProps = {}) {
  const { orgId } = useOrgPermissions();
  const qc = useQueryClient();
  const { toast } = useToast();

  const [search, setSearch] = useState("");
  const [filterPlatform, setFilterPlatform] = useState("all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ title: "", content: "", platform: "all", category: "Caption", tags: "" });
  const [editForm, setEditForm] = useState({ title: "", content: "", platform: "all", category: "Caption", tags: "" });

  const { data: templates = [], refetch } = useQuery({
    queryKey: ["content-library", orgId],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return [];
      let q = (supabase as any)
        .from("content_templates")
        .select("*")
        .order("use_count", { ascending: false });
      if (orgId) q = q.eq("org_id", orgId);
      else q = q.eq("user_id", user.id);
      const { data } = await q;
      return (data ?? []) as Template[];
    },
  });

  const handleAdd = async () => {
    if (!form.title.trim() || !form.content.trim()) return;
    const { data: { user } } = await supabase.auth.getUser();
    const tags = form.tags.split(",").map(t => t.trim()).filter(Boolean);
    const { error } = await (supabase as any).from("content_templates").insert({
      user_id: user?.id,
      org_id: orgId,
      title: form.title.trim(),
      content: form.content.trim(),
      platform: form.platform,
      category: form.category,
      tags,
      use_count: 0,
    });
    if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    setForm({ title: "", content: "", platform: "all", category: "Caption", tags: "" });
    setAdding(false);
    refetch();
    toast({ title: "Template saved" });
  };

  const handleSaveEdit = async (id: string) => {
    const tags = editForm.tags.split(",").map(t => t.trim()).filter(Boolean);
    await (supabase as any).from("content_templates").update({
      title: editForm.title, content: editForm.content,
      platform: editForm.platform, category: editForm.category, tags,
    }).eq("id", id);
    setEditId(null);
    refetch();
    toast({ title: "Template updated" });
  };

  const handleDelete = async (id: string) => {
    await (supabase as any).from("content_templates").delete().eq("id", id);
    refetch();
  };

  const handleCopy = async (t: Template) => {
    await navigator.clipboard.writeText(t.content);
    await (supabase as any).from("content_templates").update({ use_count: (t.use_count ?? 0) + 1 }).eq("id", t.id);
    refetch();
    toast({ title: "Copied to clipboard", description: `"${t.title}" copied. Paste it into your composer.` });
  };

  const filtered = templates.filter(t => {
    const matchSearch = !search || t.title.toLowerCase().includes(search.toLowerCase()) || t.content.toLowerCase().includes(search.toLowerCase());
    const matchPlatform = filterPlatform === "all" || t.platform === filterPlatform || t.platform === "all";
    const matchCat = filterCategory === "all" || t.category === filterCategory;
    return matchSearch && matchPlatform && matchCat;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-primary" /> Content Library
          </h2>
          <p className="text-sm text-muted-foreground">Saved captions, hooks, CTAs, and templates — copy into your composer instantly</p>
        </div>
        <Button size="sm" onClick={() => setAdding(true)}>
          <Plus className="w-3.5 h-3.5 mr-1.5" /> New Template
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2">
        <div className="relative flex-1 min-w-40">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input
            placeholder="Search templates…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>
        <Select value={filterPlatform} onValueChange={setFilterPlatform}>
          <SelectTrigger className="w-32 h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All platforms</SelectItem>
            {["instagram","twitter","linkedin","facebook","threads"].map(p => (
              <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-32 h-8 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Add form */}
      {adding && (
        <Card className="p-5 border-primary/30 bg-primary/5 space-y-3">
          <h3 className="font-semibold text-sm">New Template</h3>
          <Input placeholder="Template name *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} />
          <Textarea
            placeholder="Content *"
            value={form.content}
            onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
            rows={4}
            className="resize-none"
          />
          <div className="grid grid-cols-2 gap-3">
            <Select value={form.platform} onValueChange={v => setForm(f => ({ ...f, platform: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All platforms</SelectItem>
                {["instagram","twitter","linkedin","facebook","threads"].map(p => (
                  <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={form.category} onValueChange={v => setForm(f => ({ ...f, category: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Input placeholder="Tags (comma-separated)" value={form.tags} onChange={e => setForm(f => ({ ...f, tags: e.target.value }))} />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleAdd} disabled={!form.title || !form.content}>Save Template</Button>
            <Button size="sm" variant="outline" onClick={() => setAdding(false)}>Cancel</Button>
          </div>
        </Card>
      )}

      {/* Template grid */}
      {filtered.length === 0 ? (
        <Card className="p-10 text-center">
          <BookOpen className="w-10 h-10 mx-auto mb-3 text-muted-foreground/30" />
          <p className="font-medium text-muted-foreground">
            {templates.length === 0 ? "No templates yet" : "No templates match your filters"}
          </p>
          <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
            {templates.length === 0
              ? "Save your best captions, hooks, and CTAs here for reuse."
              : "Try adjusting your search or filters."}
          </p>
          {templates.length === 0 && (
            <Button className="mt-4" size="sm" onClick={() => setAdding(true)}>
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add First Template
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {filtered.map(t => {
            const Icon = PLATFORM_ICONS[t.platform] ?? BookOpen;
            const color = PLATFORM_COLORS[t.platform] ?? "#6b7280";
            const isEditing = editId === t.id;

            return (
              <Card key={t.id} className="p-4 flex flex-col gap-3">
                {isEditing ? (
                  <div className="space-y-2">
                    <Input value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} placeholder="Title" />
                    <Textarea value={editForm.content} onChange={e => setEditForm(f => ({ ...f, content: e.target.value }))} rows={4} className="resize-none" />
                    <div className="grid grid-cols-2 gap-2">
                      <Select value={editForm.platform} onValueChange={v => setEditForm(f => ({ ...f, platform: v }))}>
                        <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All platforms</SelectItem>
                          {["instagram","twitter","linkedin","facebook","threads"].map(p => (
                            <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={editForm.category} onValueChange={v => setEditForm(f => ({ ...f, category: v }))}>
                        <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <Input value={editForm.tags} onChange={e => setEditForm(f => ({ ...f, tags: e.target.value }))} placeholder="Tags (comma-separated)" />
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleSaveEdit(t.id)}><Check className="w-3.5 h-3.5 mr-1" />Save</Button>
                      <Button size="sm" variant="outline" onClick={() => setEditId(null)}><X className="w-3.5 h-3.5 mr-1" />Cancel</Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white shrink-0" style={{ background: color }}>
                          <Icon className="w-3.5 h-3.5" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-sm truncate">{t.title}</p>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <Badge variant="secondary" className="text-[10px]">{t.category}</Badge>
                            {t.platform !== "all" && (
                              <Badge variant="outline" className="text-[10px] capitalize">{t.platform}</Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-1 shrink-0">
                        <Button size="icon" variant="ghost" className="w-6 h-6" onClick={() => {
                          setEditId(t.id);
                          setEditForm({ title: t.title, content: t.content, platform: t.platform, category: t.category, tags: (t.tags ?? []).join(", ") });
                        }}>
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button size="icon" variant="ghost" className="w-6 h-6 text-destructive hover:text-destructive" onClick={() => handleDelete(t.id)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>

                    <p className="text-sm text-muted-foreground line-clamp-3 leading-relaxed flex-1">{t.content}</p>

                    {(t.tags ?? []).length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {t.tags.map((tag: string) => (
                          <span key={tag} className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground flex items-center gap-0.5">
                            <Tag className="w-2.5 h-2.5" />{tag}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-2 border-t border-border">
                      <span className="text-[10px] text-muted-foreground">
                        Used {t.use_count ?? 0}× · {formatDistanceToNow(new Date(t.created_at), { addSuffix: true })}
                      </span>
                      <div className="flex gap-1.5">
                        {onUseInCompose && (
                          <Button
                            size="sm"
                            className="h-7 text-xs"
                            onClick={async () => {
                              await (supabase as any).from("content_templates").update({ use_count: (t.use_count ?? 0) + 1 }).eq("id", t.id);
                              refetch();
                              onUseInCompose(t.content);
                            }}
                          >
                            <PenSquare className="w-3 h-3 mr-1" /> Use in Compose
                          </Button>
                        )}
                        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleCopy(t)}>
                          <Copy className="w-3 h-3 mr-1" /> Copy
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
